import { useState, useEffect } from 'react';
import { ordersAPI, measurementsAPI, settingsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Ruler, ChevronDown, ChevronUp, Calendar, Check, Loader2 } from 'lucide-react';

const GARMENT_LABELS = {
  suit: 'Suit', jacket: 'Jacket', shirt: 'Shirt', sherwani: 'Sherwani',
  bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta', trousers: 'Trousers',
};

export default function StepOrder({ wizardState, updateState, nextStep, prevStep }) {
  const [measurementMode, setMeasurementMode] = useState(null); // null | 'now' | 'later'
  const [measurementFields, setMeasurementFields] = useState([]);
  const [measurementValues, setMeasurementValues] = useState({});
  const [loadingFields, setLoadingFields] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [creating, setCreating] = useState(false);
  const [requiredByDate, setRequiredByDate] = useState('');

  const garmentType = wizardState.selectedGarmentType || 'suit';
  const fabric = wizardState.selectedFabric;

  // Load measurement fields when "Take Measurements Now" is chosen
  useEffect(() => {
    if (measurementMode === 'now') {
      loadMeasurementFields();
    }
  }, [measurementMode]);

  const loadMeasurementFields = async () => {
    setLoadingFields(true);
    try {
      const res = await measurementsAPI.getFields(garmentType);
      setMeasurementFields(res.data || []);
    } catch {
      toast.error('Failed to load measurement fields');
    }
    setLoadingFields(false);
  };

  // Key fields for the garment type (simplified view)
  const KEY_FIELDS = {
    suit: ['chest', 'waist', 'hips', 'shoulder_width', 'sleeve_length', 'jacket_length', 'trouser_waist', 'inseam'],
    jacket: ['chest', 'waist', 'shoulder_width', 'sleeve_length', 'jacket_length'],
    shirt: ['chest', 'waist', 'neck', 'shoulder_width', 'sleeve_length', 'shirt_length'],
    sherwani: ['chest', 'waist', 'hips', 'shoulder_width', 'sleeve_length', 'kurta_length'],
    bandhgala: ['chest', 'waist', 'shoulder_width', 'sleeve_length', 'jacket_length'],
    nehru_vest: ['chest', 'waist', 'shoulder_width', 'waistcoat_length'],
    kurta: ['chest', 'waist', 'shoulder_width', 'sleeve_length', 'kurta_length'],
    trousers: ['trouser_waist', 'hips', 'thigh', 'knee', 'inseam', 'trouser_length'],
  };

  const keyFieldNames = KEY_FIELDS[garmentType] || KEY_FIELDS.suit;
  const keyFields = measurementFields.filter(f => keyFieldNames.includes(f.field_name));
  const advancedFields = measurementFields.filter(f => !keyFieldNames.includes(f.field_name));

  const handleMeasurementChange = (fieldName, value) => {
    setMeasurementValues(prev => ({ ...prev, [fieldName]: parseFloat(value) || 0 }));
  };

  const createOrder = async () => {
    if (!fabric || !wizardState.client) {
      toast.error('Missing fabric or client info');
      return;
    }

    setCreating(true);
    try {
      // Save measurements if taken
      if (measurementMode === 'now' && Object.keys(measurementValues).length > 0) {
        const values = Object.entries(measurementValues)
          .filter(([_, v]) => v > 0)
          .map(([field_name, value]) => ({
            field_name,
            value_inches: value,
            value_cm: Math.round(value * 2.54 * 10) / 10,
          }));

        if (values.length > 0) {
          await measurementsAPI.create(wizardState.client.id, {
            garment_context: garmentType,
            measured_by: 'Client Mode',
            values,
          });
        }
      }

      // Create order
      const orderData = {
        client_id: wizardState.client.id,
        order_date: new Date().toISOString().split('T')[0],
        required_by_date: requiredByDate || undefined,
        notes: `Created via Client Mode consultation`,
        total_price_override: wizardState.inclusivePrice || undefined,
        items: [{
          garment_type: garmentType,
          fabric_id: fabric.id,
          fabric_quantity_metres: garmentType === 'suit' ? 4.5 : garmentType === 'shirt' ? 2.5 : garmentType === 'trousers' ? 1.8 : 3.5,
          customisation_spec: wizardState.customisation || {},
        }],
      };

      const res = await ordersAPI.create(orderData);
      updateState({ orderId: res.data?.id || res.data?.order_id });
      toast.success('Order created!');
      nextStep();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create order';
      toast.error(msg);
    }
    setCreating(false);
  };

  // Description line
  const descLine = [
    fabric?.colour,
    fabric?.pattern,
    GARMENT_LABELS[garmentType],
  ].filter(Boolean).join(' ');

  return (
    <div className="max-w-2xl mx-auto px-6 py-10" data-testid="step-order">
      <div className="text-center mb-8">
        <h1 className="text-3xl lg:text-4xl font-light tracking-tight" style={{ color: 'var(--brand-navy)', fontFamily: "'Playfair Display', serif" }}>
          Your Order
        </h1>
        <p className="mt-2 text-base" style={{ color: 'var(--ink-muted)' }}>
          Almost there - let's get the details right
        </p>
      </div>

      {/* Measurement choice */}
      {measurementMode === null && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          <button
            onClick={() => setMeasurementMode('now')}
            className="p-6 rounded-2xl border-2 text-left transition-all hover:shadow-lg"
            style={{ borderColor: 'rgba(27,42,74,0.08)', backgroundColor: 'white' }}
            data-testid="measurements-now-btn"
          >
            <Ruler className="w-8 h-8 mb-3" style={{ color: 'var(--brand-gold)' }} />
            <p className="text-base font-semibold" style={{ color: 'var(--brand-navy)' }}>Take Measurements Now</p>
            <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>Quick measurement form for your garment</p>
          </button>
          <button
            onClick={() => setMeasurementMode('later')}
            className="p-6 rounded-2xl border-2 text-left transition-all hover:shadow-lg"
            style={{ borderColor: 'rgba(27,42,74,0.08)', backgroundColor: 'white' }}
            data-testid="measurements-later-btn"
          >
            <Calendar className="w-8 h-8 mb-3" style={{ color: 'var(--ink-muted)' }} />
            <p className="text-base font-semibold" style={{ color: 'var(--brand-navy)' }}>I'll Send Measurements Later</p>
            <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>Skip to customisation and order summary</p>
          </button>
        </div>
      )}

      {/* Measurement form */}
      {measurementMode === 'now' && (
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>
              Measurements for {GARMENT_LABELS[garmentType]}
            </h2>
            <button onClick={() => setMeasurementMode(null)} className="text-xs" style={{ color: 'var(--ink-muted)' }}>Change</button>
          </div>

          {loadingFields ? (
            <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                {keyFields.map(field => (
                  <div key={field.field_name}>
                    <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                      {field.label || field.field_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} (inches)
                    </Label>
                    <Input
                      type="number"
                      step="0.25"
                      inputMode="decimal"
                      value={measurementValues[field.field_name] || ''}
                      onChange={e => handleMeasurementChange(field.field_name, e.target.value)}
                      className="h-12 rounded-xl text-lg mt-1"
                      placeholder="0"
                      data-testid={`measurement-${field.field_name}`}
                    />
                  </div>
                ))}
              </div>

              {advancedFields.length > 0 && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center gap-1 text-xs font-medium py-2"
                    style={{ color: 'var(--ink-muted)' }}
                  >
                    {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {showAdvanced ? 'Hide' : 'Show'} Additional Measurements ({advancedFields.length})
                  </button>
                  {showAdvanced && (
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      {advancedFields.map(field => (
                        <div key={field.field_name}>
                          <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                            {field.label || field.field_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} (inches)
                          </Label>
                          <Input
                            type="number"
                            step="0.25"
                            inputMode="decimal"
                            value={measurementValues[field.field_name] || ''}
                            onChange={e => handleMeasurementChange(field.field_name, e.target.value)}
                            className="h-12 rounded-xl text-lg mt-1"
                            placeholder="0"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Measurement skipped indicator */}
      {measurementMode === 'later' && (
        <div className="mb-10 p-4 rounded-2xl text-center" style={{ backgroundColor: 'rgba(27,42,74,0.03)', border: '1px solid rgba(27,42,74,0.06)' }}>
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
            <Check className="w-4 h-4 inline mr-1" style={{ color: 'var(--brand-gold)' }} />
            Measurements will be taken at a later appointment
          </p>
          <button onClick={() => setMeasurementMode(null)} className="text-xs mt-1 underline" style={{ color: 'var(--ink-muted)' }}>Change</button>
        </div>
      )}

      {/* Order Summary */}
      {measurementMode !== null && (
        <div className="space-y-6">
          {/* Required by date */}
          <div>
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>When Do You Need This By?</Label>
            <Input
              type="date"
              value={requiredByDate}
              onChange={e => setRequiredByDate(e.target.value)}
              className="h-12 rounded-xl text-base mt-1 max-w-xs"
              min={new Date().toISOString().split('T')[0]}
              data-testid="required-by-date"
            />
          </div>

          {/* Summary card */}
          <div className="p-6 rounded-2xl" style={{ backgroundColor: 'white', border: '1px solid rgba(27,42,74,0.08)' }}
            data-testid="order-summary-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {(fabric?.swatch_image_url || fabric?.swatch_url) && (
                  <img src={fabric.swatch_image_url || fabric.swatch_url} alt="" className="w-14 h-14 rounded-xl object-cover" />
                )}
                <div>
                  <p className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>{descLine}</p>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                    For {wizardState.client?.first_name} {wizardState.client?.last_name}
                    {requiredByDate && ` · Due: ${new Date(requiredByDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                  </p>
                </div>
              </div>
              <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                &#8377;{wizardState.inclusivePrice?.toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          {/* Confirm Order */}
          <Button
            onClick={createOrder}
            disabled={creating}
            className="w-full h-14 rounded-full text-base font-semibold gap-2 shadow-lg"
            style={{ backgroundColor: 'var(--brand-navy)' }}
            data-testid="confirm-order-btn"
          >
            {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
            {creating ? 'Creating Order...' : 'Confirm Order'}
          </Button>

          <Button variant="ghost" onClick={prevStep} className="w-full gap-2 text-xs" style={{ color: 'var(--ink-muted)' }}>
            <ArrowLeft className="w-3 h-3" /> Back to Try-On
          </Button>
        </div>
      )}

      {/* Back when no mode selected */}
      {measurementMode === null && (
        <Button variant="ghost" onClick={prevStep} className="gap-2 text-xs" style={{ color: 'var(--ink-muted)' }}>
          <ArrowLeft className="w-3 h-3" /> Back
        </Button>
      )}
    </div>
  );
}
